import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import showConfirmModal from 'components/ConfirmModal';
import SpinnerModal from 'components/SpinnerModal';
import LoadingSpinner from 'components/LoadingSpinner';
import ReportProblemIcon from 'assets/report_problem.svg?react';
import useUnsavedChangesGuard from 'hooks/useUnsavedChangesGuard';
import ProgressStepper, {
  ProgressStepperStep
} from 'components/RecordsListView/ProgressStepper';
import {
  initialDetailsValues,
  initialPropertiesValues,
  initialProductsValues
} from '../Constants/planFormikData';
import { PlanWizardFormValues } from '../types';
import DetailsStep from './Steps/DetailsStep';
import PropertiesStep from './Steps/PropertiesStep';
import ProductsStep from './Steps/ProductsStep';
import { PropertyTreeProvider } from './PropertyTreeContext';
import { useCropDataContext } from './CropDataContext';

interface PlanStepsLayoutProps {
  /**
   * Submits the wizard's current Formik values (PR5) — the mutation itself
   * (create/edit/duplicate) is resolved by the caller (`PlanRouteAdmin`) via
   * `usePlanWizardMutations`, not here, since that hook's own fetch (edit/
   * duplicate) must resolve *before* this component's `<Formik
   * initialValues>` mounts — calling it in the parent instead of in here.
   */
  onSubmit: (values: PlanWizardFormValues) => Promise<unknown>;
  saving?: boolean;
  /** "Create Plan" / "Edit Plan" — defaults to Create for the existing entry point. */
  title?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  /**
   * True for the `edit` wizard mode only (not `create`/`duplicate`) — swaps
   * the discard-confirm modal to the Applications/Work Orders "edit" style
   * (orange warning icon, green confirm button, "…to your record?" copy)
   * matching their own `isEditMode` split. Create/duplicate keep the plain
   * default `ConfirmModal` styling.
   */
  isEditMode?: boolean;
  /**
   * FR-041 (2026-08-17 design QA): true while `PlanRouteAdmin` is still
   * fetching the source Plan for edit/duplicate. Renders the stepper shell
   * (breadcrumb/title/step nav/footer) immediately with an inline loading
   * placeholder in place of the current step's real fields, rather than
   * `PlanRouteAdmin`'s previous full-screen "Loading Plan" modal blocking
   * navigation into the wizard entirely.
   */
  loadingSource?: boolean;
  /**
   * True when `PlanRouteAdmin`'s source-Plan fetch (edit/duplicate) finished
   * without returning a Plan — keeps the wizard blocked instead of silently
   * continuing with blank/default values against the requested id.
   */
  loadError?: boolean;
}

/**
 * Inline stand-in for the Details step's real content while loading (FR-041)
 * — the exact `LoadingFirstStep` pattern Applications/Work Orders' own
 * `StepsLayout` uses (`components/LoadingSpinner` in a white, rounded,
 * shadowed card, centered over the step area), so Plans' edit/duplicate
 * loading state is visually identical to theirs rather than a bespoke one.
 */
const LoadingFirstStep: React.FC = () => (
  <div
    className="w-full h-full flex items-center justify-center"
    data-testid="plan-steps-layout-loading"
  >
    <LoadingSpinner className="w-60 py-8 px-4 rounded-lg bg-white shadow-md" />
  </div>
);

/**
 * Shown in place of `LoadingFirstStep` when the source-Plan fetch (edit/
 * duplicate) fails — `usePlanData` has no refetch, so the only way out is
 * the stepper's always-present footer Cancel button back to the Plans list.
 */
const LoadErrorFirstStep: React.FC = () => (
  <div
    className="w-full h-full flex items-center justify-center"
    data-testid="plan-steps-layout-load-error"
  >
    <div className="py-8 px-6 rounded-lg bg-white shadow-md text-center max-w-sm">
      <p className="text-sm font-semibold text-neutral-1000 mb-1">
        Unable to load this Plan
      </p>
      <p className="text-sm text-neutral-60">
        Something went wrong loading its details. Go back to Plans and try
        again.
      </p>
    </div>
  </div>
);

const showEditDiscardConfirmModal = (onConfirm: () => void) =>
  showConfirmModal({
    icon: <ReportProblemIcon />,
    title: 'Discard changes to your record?',
    content: <>None of the changes to this record will be saved.</>,
    okText: 'Discard changes',
    okButtonProps: {
      style: {
        borderRadius: 4,
        background: 'var(--Green-green-60, #14803C)',
        padding: '10px 16px',
        color: '#FFF',
        fontSize: 14,
        fontWeight: 400,
        marginLeft: 8
      }
    },
    cancelText: 'Continue editing',
    onOk: onConfirm
  });

const showCreateDiscardConfirmModal = (onConfirm: () => void) =>
  showConfirmModal({
    title: 'Discard Plan?',
    content: (
      <>
        By leaving the page, you will lose{' '}
        <span className="font-semibold">all data</span> from this record.
      </>
    ),
    okText: 'Discard record',
    cancelText: 'Continue editing',
    onOk: onConfirm
  });

/**
 * Plan creation wizard shell (FR-003/004/005): Details → Properties →
 * Products, adapting `ProgressStepper` exactly as Invoices' own
 * `redesign/StepsLayout` does — no separate wizard-state Context provider is
 * needed (confirmed against the shipped Invoices code; see research.md's
 * "Decision: Wizard shell"), just local `currentStep` state plus the
 * `usePlanWizardMutations` submit hook.
 */
const PlanStepsLayout: React.FC<PlanStepsLayoutProps> = ({
  onSubmit,
  saving = false,
  title = 'Create Plan',
  onSuccess,
  onCancel,
  loadingSource = false,
  loadError = false,
  isEditMode = false
}) => {
  const { values, dirty } = useFormikContext<PlanWizardFormValues>();
  const [currentStep, setCurrentStep] = useState(0);
  // Folds in CropDataContext's fetch (shared with DetailsStep) so Next/Save
  // stay disabled until both the source-Plan and crop-list fetches resolve.
  const { loading: cropsLoading } = useCropDataContext();
  const isLoading = loadingSource || Boolean(cropsLoading);
  const isBlocked = isLoading || loadError;
  let firstStepComponent: React.ComponentType = DetailsStep;
  if (loadError) firstStepComponent = LoadErrorFirstStep;
  else if (isLoading) firstStepComponent = LoadingFirstStep;

  const showDiscardConfirmModal = isEditMode
    ? showEditDiscardConfirmModal
    : showCreateDiscardConfirmModal;

  const { allowNavigation } = useUnsavedChangesGuard({
    isDirty: dirty,
    isSaving: saving,
    showDiscardModal: showDiscardConfirmModal
  });

  const handleSave = async () => {
    // `usePlanData.savePlan`'s promise chain (`.catch(parseServerError(dispatch))`)
    // never rethrows on a failed save — it resolves to `undefined` after
    // dispatching a global error banner. Matches `Plan/index.jsx`'s own
    // `const success = await savePlan(...); if (success) {...}` guard — without
    // it, a failed save would still navigate away and permanently disable the
    // unsaved-changes guard as if it had succeeded.
    const result = await onSubmit(values);
    if (!result) return;
    allowNavigation();
    if (onSuccess) onSuccess();
  };

  const handleCancel = () => {
    const confirmAndCancel = () => {
      allowNavigation();
      if (onCancel) onCancel();
    };
    if (!dirty) {
      confirmAndCancel();
      return;
    }
    showDiscardConfirmModal(confirmAndCancel);
  };

  const handleStepChange = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const saveButtonContent = (
    <span className="w-full text-center">Save Plan</span>
  );

  const steps: ProgressStepperStep[] = [
    {
      name: 'Plan Details',
      // Only the first step swaps to the loading card — matches
      // Applications/Work Orders' `LoadingFirstStep` naming/behavior exactly
      // (Properties/Products stay their real components; they're just
      // unreachable while `disableStepButtons` is true below).
      component: firstStepComponent,
      initialValues: initialDetailsValues,
      nextButtonLabel: 'Select Properties',
      ...(isEditMode ? { showButton: true, handleSave, saveButtonContent } : {})
    },
    {
      name: 'Select Properties',
      component: PropertiesStep,
      initialValues: initialPropertiesValues,
      backButtonLabel: 'Plan Details',
      nextButtonLabel: 'Select Products',
      ...(isEditMode ? { showButton: true, handleSave, saveButtonContent } : {})
    },
    {
      name: 'Select Products',
      component: ProductsStep,
      initialValues: initialProductsValues,
      backButtonLabel: 'Select Properties',
      showButton: true,
      handleSave,
      saveButtonContent
    }
  ];

  return (
    <PropertyTreeProvider>
      <div className="h-full w-full relative">
        <Breadcrumb
          hideOrganizationDropdown={false}
          hideCropSeasonDropdown={false}
          disabledCropSeasonCreate
          allowMultipleCropSeasonSelection
          disabled
        >
          <Breadcrumb.Item title="Plans" value={title} isLast />
        </Breadcrumb>
        <ProgressStepper
          stepperName={title}
          steps={steps}
          currentStep={currentStep}
          onStepChange={handleStepChange}
          disableSaveButton={saving || isBlocked}
          disableStepButtons={saving || isBlocked}
          onCancel={handleCancel}
        />
        <SpinnerModal open={saving} message="Saving Plan…" />
      </div>
    </PropertyTreeProvider>
  );
};

export default PlanStepsLayout;
